import { Component, type ErrorInfo, type ReactNode } from 'react';
import { captureException } from '../lib/telemetry';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  message: string | null;
  /** Stale-chunk failures get a distinct copy + CTA from generic crashes. */
  isChunkError: boolean;
}

// Detect chunk-load failures emitted by dynamic `import()` after a deploy
// invalidates the previously fetched chunk hashes. React.lazy surfaces these
// through the nearest error boundary. Different bundlers use different
// signals (Vite/Rollup throw `TypeError: Failed to fetch dynamically imported
// module …`; webpack throws an Error with `name === 'ChunkLoadError'`).
// Match both so the boundary degrades gracefully across tooling.
function isChunkLoadError(err: unknown): boolean {
  if (!(err instanceof Error)) return false;
  if (err.name === 'ChunkLoadError') return true;
  const msg = err.message ?? '';
  return (
    /Loading chunk [\d]+ failed/i.test(msg)
    || /Failed to fetch dynamically imported module/i.test(msg)
    || /error loading dynamically imported module/i.test(msg)
    || /Importing a module script failed/i.test(msg)
  );
}

// Top-level error boundary. Catches render-time exceptions in any descendant
// and renders a graceful fallback instead of React's default error UI.
//
// We intentionally do NOT log error contents to the page (the user already
// knows something broke) and we do NOT include any PII. Errors forward to
// the telemetry sink (Sentry when VITE_SENTRY_DSN is configured; console-only
// in dev). See src/lib/telemetry.ts.
//
// Chunk-load failures (typical after a deploy invalidates the user's cached
// chunk hashes) get a distinct "Reload to update" message that's both more
// accurate and less alarming than the generic "Something broke" copy.
export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, message: null, isChunkError: false };

  static getDerivedStateFromError(err: Error): State {
    return {
      hasError: true,
      message: err.message || null,
      isChunkError: isChunkLoadError(err),
    };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Chunk-load errors are operationally expected after a deploy; flag the
    // telemetry event so we can spot a sudden spike (which usually means a
    // bad cache header on the CDN, not a true bug).
    captureException(error, {
      componentStack: info.componentStack ?? null,
      chunkLoad: isChunkLoadError(error),
    });
    if (typeof console !== 'undefined') {
      console.error('[ErrorBoundary]', error);
    }
  }

  reset = () => {
    this.setState({ hasError: false, message: null, isChunkError: false });
    // Hard reload to clear any corrupted in-memory state (router cache,
    // auth tokens that failed mid-refresh, etc.). For chunk-load failures
    // this also forces the browser to refetch the (now-current) index.html
    // and pick up the new chunk hashes.
    if (typeof window !== 'undefined') {
      window.location.reload();
    }
  };

  render() {
    if (this.state.hasError) {
      const chunk = this.state.isChunkError;
      return (
        <main
          id="main-content"
          style={{
            minBlockSize: '100dvh',
            display: 'grid',
            placeItems: 'center',
            padding: 'var(--space-5)',
            background: 'var(--color-bg-canvas)',
            color: 'var(--color-ink-primary)',
          }}
        >
          <div style={{ maxInlineSize: '480px', textAlign: 'center' }}>
            <h1
              className="text-h1"
              style={{ fontWeight: 500, marginBlockEnd: 'var(--space-3)' }}
            >
              {chunk ? 'A new version is available.' : 'Something broke.'}
            </h1>
            <p className="muted" style={{ marginBlockEnd: 'var(--space-5)' }}>
              {chunk ? (
                <>
                  We couldn&apos;t load part of the app — usually because a new
                  build was deployed while you were using the old one. Reload
                  to update — your data is safe.
                </>
              ) : (
                <>
                  We hit an unexpected error. Refresh to try again — your data
                  is safe. If this keeps happening, please open an issue on{' '}
                  <a
                    href="https://github.com/justinwhitton/seven-oh/issues"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: 'var(--color-accent)' }}
                  >
                    GitHub
                  </a>
                  .
                </>
              )}
            </p>
            <button
              type="button"
              onClick={this.reset}
              className="btn btn-primary btn-lg"
            >
              {chunk ? 'Reload to update' : 'Reload the page'}
            </button>
            <p
              className="dim text-caption"
              style={{ marginBlockStart: 'var(--space-6)' }}
            >
              In crisis? Call or text 988 (US) any time.
            </p>
          </div>
        </main>
      );
    }
    return this.props.children;
  }
}
